// =============================================================================
// Visiteurs Manager — Uniformisé sur OHGrid v1.1.1 (GROOT UI-002/004/005/006/007)
// Stats agrégées par visiteur + CRUD + archivage + bulk actions
// =============================================================================
import { useState, useEffect, useCallback, useMemo } from "react";
import {
  Users, UserPlus, Archive, ArchiveRestore, Trash2, Eye, Mail, Phone,
  FileText, Euro, CheckCircle2, XCircle, Loader2, ArrowLeft, Edit3, Save, X,
  User, ChevronDown,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";
import { OHGrid } from "../../components/ui/oh-grid";
import { normalizeName } from "../../lib/normalize";
// 🔴🔴 N°728-1 — « TEF payé » / « Visiteurs AMO » / « AMO total » / « Montant AMO »
// étaient affichés à TOUT tenant, ⛔ sans AUCUNE condition. Mesuré en PROD par
// INTÉGRATION avec un compte `mario_bat` : un artisan lisait le NOM D'UN AUTRE
// TENANT sur son écran.
// 🔑 Ce qui décide est la CAPACITÉ DÉCLARÉE `dossier_amo`, ⛔ jamais un slug :
// « TEF payé » vient de `hub_grids.py:1077 tef_paye_visiteur`, le flux SORTANT
// « le donneur d'ordre a versé AU visiteur » — un geste d'AMO. Un artisan qui
// visite ses propres chantiers ⛔ n'a PERSONNE à payer.
import { hasDossierAmo } from "../../utils/tenantProfile";
import { statutBadgeStyle } from "../../lib/statutBadge";

const API = process.env.REACT_APP_BACKEND_URL;
const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("session_token")}`,
  "Content-Type": "application/json",
});

function BoolBadge({ ok, label }) {
  return (
    <span className="inline-flex items-center gap-1 text-xs">
      {ok ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
          : <XCircle className="w-3.5 h-3.5" style={{ color: "var(--oh-text-muted)" }} />}
      <span style={{ color: ok ? "var(--oh-text)" : "var(--oh-text-muted)" }}>{label}</span>
    </span>
  );
}

function ProgressMini({ pct }) {
  return (
    <div className="w-12 h-1.5 rounded-full hidden sm:block" style={{ background: "var(--oh-border)" }}>
      <div className="h-full rounded-full transition-all" style={{
        width: `${pct}%`,
        background: pct >= 80 ? "#10b981" : pct >= 50 ? "var(--oh-stat-amber-text)" : "var(--oh-stat-red-text)",
      }} />
    </div>
  );
}

// ─── Sous-composants STABLES (définis HORS de VisiteurFiche) ────────────
// Cf. CLAUDE.md "Fichiers protégés" / fix FinancementTab.jsx :
// définir EditField/InfoField INSIDE VisiteurFiche les recrée à chaque
// render → inputs remountés → focus perdu à chaque caractère.
// (Fix bug 2026-05-20 saisie caractère par caractère.)
function InfoField({ label, value }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wide mb-0.5" style={{ color: "var(--oh-text-muted)" }}>{label}</p>
      <p className="text-sm font-medium" style={{ color: value ? "var(--oh-text)" : "var(--oh-text-muted)" }}>{value || "—"}</p>
    </div>
  );
}

function EditField({ label, value, onChange, onBlur, type = "text", placeholder }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-wide mb-1 block" style={{ color: "var(--oh-text-muted)" }}>{label}</label>
      {type === "textarea" ? (
        <textarea value={value} onChange={e => onChange(e.target.value)}
          onBlur={onBlur ? e => onBlur(e.target.value) : undefined}
          rows={2} placeholder={placeholder}
          className="w-full px-3 py-2 rounded-lg text-sm border resize-none"
          style={{ background: "var(--oh-bg-surface)", borderColor: "var(--oh-border)", color: "var(--oh-text)" }} />
      ) : (
        <input type={type} value={value} onChange={e => onChange(e.target.value)}
          onBlur={onBlur ? e => onBlur(e.target.value) : undefined}
          placeholder={placeholder}
          className="w-full px-3 py-2 rounded-lg text-sm border"
          style={{ background: "var(--oh-bg-surface)", borderColor: "var(--oh-border)", color: "var(--oh-text)" }} />
      )}
    </div>
  );
}

// ─── Fiche détail visiteur ─────────────────────────────────────────────
function VisiteurFiche({ visiteurName, onBack, onRefreshList }) {
  const [loading, setLoading] = useState(true);
  const [visiteur, setVisiteur] = useState(null);
  const [dossiers, setDossiers] = useState({ count: 0, dossiers: [] });
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});

  // Fetch op_visiteurs record matching the visiteur name. Reused
  // par load initial et reload post-save (pour garantir que l'affichage
  // reflète l'état canonique DB, pas un merge local possiblement
  // incomplet si le visiteur n'avait pas été chargé au préalable).
  const fetchVisiteur = useCallback(async (name) => {
    const res = await fetch(`${API}/api/metier/op-visiteurs?search=${encodeURIComponent(name)}&include_archived=true`, {
      headers: getAuthHeaders(), credentials: "include",
    });
    if (!res.ok) return null;
    const list = await res.json();
    const q = (name || "").toLowerCase().trim();
    // Match exact prioritaire, fuzzy en fallback
    return list.find(v => {
      const full = `${v.prenom || ""} ${v.nom || ""}`.trim().toLowerCase();
      return full === q;
    }) || list.find(v => {
      const full = `${v.prenom || ""} ${v.nom || ""}`.trim().toLowerCase();
      return full.includes(q) || q.includes(full);
    }) || null;
  }, []);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const match = await fetchVisiteur(visiteurName);
        if (match) setVisiteur(match);
        const dRes = await fetch(
          `${API}/api/metier/hub-grids/visiteurs-stats/${encodeURIComponent(visiteurName)}/dossiers`,
          { headers: getAuthHeaders(), credentials: "include" }
        );
        if (dRes.ok) setDossiers(await dRes.json());
      } catch { toast.error("Erreur réseau"); }
      finally { setLoading(false); }
    };
    load();
  }, [visiteurName, fetchVisiteur]);

  const startEdit = () => {
    setForm({
      nom: visiteur?.nom || "", prenom: visiteur?.prenom || "",
      email: visiteur?.email || "", telephone: visiteur?.telephone || "",
      iban: visiteur?.iban || "", bic: visiteur?.bic || "",
      tarif_visite: visiteur?.tarif_visite ?? "",
      notes: visiteur?.notes || "",
      zones: (visiteur?.zones || []).join(", "),
      specialites: (visiteur?.specialites || []).join(", "),
    });
    setEditing(true);
  };

  const saveVisiteur = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        zones: form.zones ? form.zones.split(",").map(s => s.trim()).filter(Boolean) : [],
        specialites: form.specialites ? form.specialites.split(",").map(s => s.trim()).filter(Boolean) : [],
        // Tarif visite (€) : nombre ou null (jamais "" → le backend Float rejette).
        tarif_visite: (form.tarif_visite === "" || form.tarif_visite == null) ? null : parseFloat(form.tarif_visite),
      };
      const res = await fetch(`${API}/api/metier/op-visiteurs/${encodeURIComponent(visiteurName)}`, {
        method: "PATCH", headers: getAuthHeaders(), credentials: "include",
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        toast.success("Visiteur mis à jour");
        // Re-fetch DB pour récupérer l'état canonique — évite que
        // le mode lecture affiche "—" si visiteur était null au
        // préalable (search backend défaillant sur "Prenom Nom") et
        // que le merge local laisse des champs vides.
        // Cherche par le nouveau nom (si renommage) sinon ancien.
        const newName = `${payload.prenom || ""} ${payload.nom || ""}`.trim() || visiteurName;
        const reloaded = await fetchVisiteur(newName);
        if (reloaded) {
          setVisiteur(reloaded);
        } else {
          // Fallback : merger localement si le re-fetch échoue
          setVisiteur(prev => ({ ...(prev || {}), ...payload }));
        }
        setEditing(false);
        onRefreshList?.();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Erreur sauvegarde");
      }
    } catch { toast.error("Erreur réseau"); }
    finally { setSaving(false); }
  };

  const toggleArchive = async () => {
    const isArchived = visiteur?.archived === true;
    if (!window.confirm(`${isArchived ? "Réactiver" : "Archiver"} ce visiteur ?`)) return;
    try {
      const res = await fetch(`${API}/api/metier/op-visiteurs/${encodeURIComponent(visiteurName)}/archive`, {
        method: "POST", headers: getAuthHeaders(), credentials: "include",
        body: JSON.stringify({ archived: !isArchived }),
      });
      if (res.ok) {
        setVisiteur(prev => ({ ...prev, archived: !isArchived }));
        toast.success(isArchived ? "Visiteur réactivé" : "Visiteur archivé");
        onRefreshList?.();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || `Erreur ${isArchived ? "réactivation" : "archivage"}`);
      }
    } catch { toast.error("Erreur réseau"); }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin" style={{ color: "var(--oh-text-muted)" }} /></div>;
  }

  return (
    <div className="space-y-4" data-testid="visiteur-fiche">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={onBack} data-testid="visiteur-back-btn"><ArrowLeft className="w-4 h-4" /></Button>
          <div className="p-2 rounded-lg bg-emerald-500/20"><Users className="w-5 h-5 text-emerald-400" /></div>
          <div>
            <h2 className="text-lg font-bold flex items-center gap-2" style={{ color: "var(--oh-text)" }}>
              {visiteur ? `${visiteur.prenom || ""} ${visiteur.nom || ""}`.trim() : visiteurName}
              {visiteur?.archived && <Badge className="bg-amber-500/20 text-amber-400 text-[10px]">Archivé</Badge>}
            </h2>
            <p className="text-xs" style={{ color: "var(--oh-text-muted)" }}>{hasDossierAmo() ? "Visiteur AMO" : "Visiteur"} · {dossiers.count} dossier{dossiers.count > 1 ? "s" : ""}</p>
          </div>
        </div>
        {!editing ? (
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={toggleArchive} data-testid="archive-visiteur-btn"
              style={{ borderColor: "var(--oh-border)", color: visiteur?.archived ? "var(--oh-text-secondary)" : "var(--oh-text-muted)" }}>
              {visiteur?.archived
                ? <><ArchiveRestore className="w-3.5 h-3.5 mr-1.5 text-blue-400" /> Réactiver</>
                : <><Archive className="w-3.5 h-3.5 mr-1.5 text-amber-400" /> Archiver</>}
            </Button>
            <Button size="sm" variant="outline" onClick={startEdit} data-testid="edit-visiteur-btn"
              style={{ borderColor: "var(--oh-border)", color: "var(--oh-text-secondary)" }}>
              <Edit3 className="w-3.5 h-3.5 mr-1.5" /> Modifier
            </Button>
          </div>
        ) : (
          <div className="flex gap-2">
            <Button size="sm" onClick={saveVisiteur} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="save-visiteur-btn">
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Save className="w-3.5 h-3.5 mr-1" />} Enregistrer
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setEditing(false)}><X className="w-3.5 h-3.5" /></Button>
          </div>
        )}
      </div>

      <div className="rounded-lg overflow-hidden" style={{ background: "var(--oh-bg-elevated)", border: "1px solid var(--oh-border)" }}>
        <details open>
          <summary className="flex items-center justify-between px-4 py-3 cursor-pointer select-none">
            <span className="text-sm font-semibold flex items-center gap-2" style={{ color: "var(--oh-text)" }}>
              <User className="w-4 h-4 text-emerald-400" /> Informations
            </span>
            <ChevronDown className="w-4 h-4" style={{ color: "var(--oh-text-muted)" }} />
          </summary>
          <div className="px-4 pb-4">
            {editing ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <EditField label="Prénom" value={form.prenom}
                  onChange={v => setForm(f => ({ ...f, prenom: v }))}
                  onBlur={v => setForm(f => ({ ...f, prenom: normalizeName(v, "prenom") }))} />
                <EditField label="Nom" value={form.nom}
                  onChange={v => setForm(f => ({ ...f, nom: v }))}
                  onBlur={v => setForm(f => ({ ...f, nom: normalizeName(v, "nom") }))} />
                <EditField label="Email" value={form.email} onChange={v => setForm(f => ({ ...f, email: v }))} type="email" />
                <EditField label="Téléphone" value={form.telephone} onChange={v => setForm(f => ({ ...f, telephone: v }))} type="tel" />
                <EditField label="IBAN" value={form.iban} onChange={v => setForm(f => ({ ...f, iban: v }))} placeholder="FR76 ..." />
                <EditField label="BIC" value={form.bic} onChange={v => setForm(f => ({ ...f, bic: v }))} />
                <EditField label="Tarif visite (€)" value={form.tarif_visite} onChange={v => setForm(f => ({ ...f, tarif_visite: v }))} type="number" placeholder="ex. 75" />
                <EditField label="Zones" value={form.zones} onChange={v => setForm(f => ({ ...f, zones: v }))} placeholder="idf, paca..." />
                <EditField label="Spécialités" value={form.specialites} onChange={v => setForm(f => ({ ...f, specialites: v }))} placeholder={hasDossierAmo() ? "AMO, ergothérapie..." : "électricité, plomberie..."} />
                <div className="sm:col-span-2">
                  <EditField label="Notes" value={form.notes} onChange={v => setForm(f => ({ ...f, notes: v }))} type="textarea" />
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                <InfoField label="Prénom" value={visiteur?.prenom} />
                <InfoField label="Nom" value={visiteur?.nom} />
                <InfoField label="Email" value={visiteur?.email} />
                <InfoField label="Téléphone" value={visiteur?.telephone} />
                <InfoField label="IBAN" value={visiteur?.iban} />
                <InfoField label="BIC" value={visiteur?.bic} />
                <InfoField label="Tarif visite" value={visiteur?.tarif_visite != null ? `${Number(visiteur.tarif_visite).toLocaleString("fr-FR")} €` : null} />
                <InfoField label="Zones" value={(visiteur?.zones || []).join(", ")} />
                <InfoField label="Spécialités" value={(visiteur?.specialites || []).join(", ")} />
                <InfoField label="Statut" value={visiteur?.status} />
                {visiteur?.notes && <div className="sm:col-span-3"><InfoField label="Notes" value={visiteur.notes} /></div>}
              </div>
            )}
          </div>
        </details>
      </div>

      <div className="rounded-lg overflow-hidden" style={{ background: "var(--oh-bg-elevated)", border: "1px solid var(--oh-border)" }}>
        <details open>
          <summary className="flex items-center justify-between px-4 py-3 cursor-pointer select-none">
            <span className="text-sm font-semibold flex items-center gap-2" style={{ color: "var(--oh-text)" }}>
              <FileText className="w-4 h-4 text-blue-400" /> Dossiers ({dossiers.count})
            </span>
            <ChevronDown className="w-4 h-4" style={{ color: "var(--oh-text-muted)" }} />
          </summary>
          <div className="px-4 pb-4">
            {/* Cumul € SORTANT du visiteur (dû/payé/reste) — barème = surcharge dossier
                sinon tarif de base (défaut 120). Distinct du Montant AMO (entrant). */}
            {(() => {
              const tarifBase = visiteur?.tarif_visite ?? 120;
              const eff = (d) => d.tarif_visite_override ?? tarifBase;
              const rows = dossiers.dossiers || [];
              const du = rows.filter(d => d.date_visite).reduce((s, d) => s + eff(d), 0);
              const paye = rows.filter(d => d.visite_payee === true).reduce((s, d) => s + eff(d), 0);
              const reste = du - paye;
              const f = (v) => `${Number(v).toLocaleString("fr-FR")} €`;
              return (
                <div className="flex flex-wrap gap-x-6 gap-y-1 mb-3 text-sm" data-testid="visiteur-cumul-euros">
                  <span style={{ color: "var(--oh-text-muted)" }}>Total dû : <b style={{ color: "var(--oh-text)" }}>{f(du)}</b></span>
                  <span style={{ color: "var(--oh-text-muted)" }}>Total payé : <b style={{ color: "#10b981" }}>{f(paye)}</b></span>
                  <span style={{ color: "var(--oh-text-muted)" }}>Reste : <b style={{ color: reste > 0 ? "var(--oh-stat-amber-text)" : "var(--oh-text-muted)" }}>{f(reste)}</b></span>
                </div>
              );
            })()}
            <div className="overflow-x-auto rounded-lg oh-responsive-table" style={{ border: "1px solid var(--oh-border)" }}>
              <table className="w-full text-sm" data-testid="visiteur-dossiers-table">
                <thead>
                  <tr style={{ background: "var(--oh-bg-surface)" }}>
                    {["Réf", "Client", "Date visite", "Statut", "Visite payée",
                      ...(hasDossierAmo() ? ["Montant AMO"] : []), "Dû au visiteur"].map(h => (
                      <th key={h} className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-left" style={{ color: "var(--oh-text-muted)" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {dossiers.dossiers.map((d, i) => (
                    <tr key={d.dossier_id || i} style={{ borderTop: "1px solid var(--oh-border)" }}>
                      <td className="px-3 py-2 font-mono text-xs" style={{ color: "var(--oh-text)" }}>{d.référence || d.reference || "—"}</td>
                      <td className="px-3 py-2" style={{ color: "var(--oh-text)" }}>{d.client_nom || "—"}</td>
                      <td className="px-3 py-2" style={{ color: "var(--oh-text-muted)" }}>{d.date_visite || "—"}</td>
                      <td className="px-3 py-2"><Badge className="text-[10px] px-1.5 py-0 border-0" style={statutBadgeStyle("zinc")}>{d.pipeline_step?.replace(/_/g, " ") || d.statut || "—"}</Badge></td>
                      <td className="px-3 py-2"><BoolBadge ok={d.visite_payee === true} label={d.visite_payee ? "Oui" : "Non"} /></td>
                      {hasDossierAmo() && (
                        <td className="px-3 py-2" style={{ color: "var(--oh-text)" }}>{d.montant_amo ? `${d.montant_amo.toLocaleString("fr-FR")} €` : "—"}</td>
                      )}
                      {/* Dû au visiteur = surcharge dossier si présente, sinon tarif de base
                          du visiteur (défaut 120). Distinct du Montant AMO (= entrant client). */}
                      <td className="px-3 py-2" style={{ color: "var(--oh-stat-amber-text)" }}>
                        {d.date_visite
                          ? `${(d.tarif_visite_override ?? visiteur?.tarif_visite ?? 120).toLocaleString("fr-FR")} €${d.tarif_visite_override != null ? " *" : ""}`
                          : "—"}
                      </td>
                    </tr>
                  ))}
                  {dossiers.dossiers.length === 0 && (
                    <tr><td colSpan={hasDossierAmo() ? 7 : 6} className="text-center py-6 text-xs" style={{ color: "var(--oh-text-muted)" }}>Aucun dossier</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </details>
      </div>
    </div>
  );
}

// ─── Composant principal (migré OHGrid) ───────────────────────────────────
export default function VisiteursManager({ onLoadVisiteurs }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ total_visiteurs: 0, total_dossiers: 0, visiteurs: [] });
  const [govVisiteurs, setGovVisiteurs] = useState([]);
  const [selectedVisiteur, setSelectedVisiteur] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ prenom: "", nom: "", email: "", telephone: "" });
  const [creating, setCreating] = useState(false);
  const [showArchived, setShowArchived] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, govRes] = await Promise.all([
        fetch(`${API}/api/metier/hub-grids/visiteurs-stats`, { headers: getAuthHeaders(), credentials: "include" }),
        fetch(`${API}/api/metier/op-visiteurs?include_archived=true`, { headers: getAuthHeaders(), credentials: "include" }),
      ]);
      if (statsRes.ok) {
        const json = await statsRes.json();
        setData(json);
        if (onLoadVisiteurs) onLoadVisiteurs(json.visiteurs);
      } else toast.error("Erreur chargement visiteurs");
      if (govRes.ok) setGovVisiteurs(await govRes.json());
    } catch { toast.error("Erreur réseau"); }
    finally { setLoading(false); }
  }, [onLoadVisiteurs]);

  useEffect(() => { loadData(); }, [loadData]);

  // Map fullname → archived
  const archivedMap = useMemo(() => {
    const m = new Map();
    govVisiteurs.forEach(v => {
      const full = `${v.prenom || ""} ${v.nom || ""}`.trim().toLowerCase();
      if (full) m.set(full, v.archived === true);
    });
    return m;
  }, [govVisiteurs]);

  // Enrichir les lignes :
  // 1. On part de data.visiteurs (stats, uniquement ceux avec dossiers)
  // 2. On AJOUTE tous les op_visiteurs absents de data.visiteurs (sans dossiers)
  // Ainsi, les archivés sans dossiers apparaissent bien dans la grille.
  const enriched = useMemo(() => {
    const statsIndex = new Map();
    (data.visiteurs || []).forEach(v => {
      statsIndex.set((v.visiteur || "").toLowerCase(), v);
    });

    const rows = [];
    const seen = new Set();

    // 1. Ajouter les visiteurs ayant des stats
    (data.visiteurs || []).forEach(v => {
      const key = (v.visiteur || "").toLowerCase();
      const archived = archivedMap.get(key) === true;
      rows.push({
        ...v,
        id: v.visiteur,
        archived,
        _pct_payee_client: v.nb_visites > 0 ? Math.round((v.visites_payees_client / v.nb_visites) * 100) : 0,
        _pct_tef_paye: v.nb_visites > 0 ? Math.round((v.tef_paye_visiteur / v.nb_visites) * 100) : 0,
      });
      seen.add(key);
    });

    // 2. Ajouter les op_visiteurs absents des stats (sans dossiers, souvent archivés)
    govVisiteurs.forEach(g => {
      const full = `${g.prenom || ""} ${g.nom || ""}`.trim();
      const key = full.toLowerCase();
      if (!full || seen.has(key)) return;
      rows.push({
        visiteur: full,
        id: full,
        archived: g.archived === true,
        nb_dossiers: 0,
        nb_visites: 0,
        visites_payees_client: 0,
        visites_non_payees_client: 0,
        tef_paye_visiteur: 0,
        tef_non_paye_visiteur: 0,
        montant_total_amo: 0,
        avec_facture: 0,
        _pct_payee_client: 0,
        _pct_tef_paye: 0,
      });
      seen.add(key);
    });

    return rows;
  }, [data.visiteurs, archivedMap, govVisiteurs]);

  // Filtrage des archivés piloté par la case "Inclure archivés" (showArchived).
  // OHGrid n'applique pas advFilterValues sur le dataset, on filtre donc ici.
  const visibleRows = useMemo(
    () => (showArchived ? enriched : enriched.filter(r => !r.archived)),
    [enriched, showArchived]
  );

  // ─── Actions unitaires ───
  const handleArchive = useCallback(async (row, archived) => {
    try {
      const res = await fetch(`${API}/api/metier/op-visiteurs/${encodeURIComponent(row.visiteur)}/archive`, {
        method: "POST", headers: getAuthHeaders(), credentials: "include",
        body: JSON.stringify({ archived }),
      });
      if (res.ok) {
        toast.success(archived ? `${row.visiteur} archivé` : `${row.visiteur} réactivé`);
        loadData();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Erreur archivage");
      }
    } catch { toast.error("Erreur réseau"); }
  }, [loadData]);

  const handleDelete = useCallback(async (row) => {
    if (!window.confirm(`Supprimer "${row.visiteur}" ?\n\nLe champ "visiteur_amo" sera vidé sur ses ${row.nb_dossiers} dossier(s).`)) return;
    try {
      const res = await fetch(`${API}/api/metier/hub-grids/visiteurs-stats/${encodeURIComponent(row.visiteur)}`, {
        method: "DELETE", headers: getAuthHeaders(), credentials: "include",
      });
      if (res.ok) {
        const r = await res.json();
        toast.success(`${row.visiteur} supprimé (${r.dossiers_modified} dossier(s) mis à jour)`);
        loadData();
      } else toast.error("Erreur suppression");
    } catch { toast.error("Erreur réseau"); }
  }, [loadData]);

  // ─── Bulk actions ───
  const bulkArchive = useCallback(async (ids, archived) => {
    if (!window.confirm(`${archived ? "Archiver" : "Réactiver"} ${ids.length} visiteur(s) ?`)) return;
    let ok = 0, errs = 0;
    for (const name of ids) {
      try {
        const res = await fetch(`${API}/api/metier/op-visiteurs/${encodeURIComponent(name)}/archive`, {
          method: "POST", headers: getAuthHeaders(), credentials: "include",
          body: JSON.stringify({ archived }),
        });
        if (res.ok) ok++; else errs++;
      } catch { errs++; }
    }
    if (ok) toast.success(`${ok} visiteur(s) traité(s)`);
    if (errs) toast.error(`${errs} erreur(s)`);
    loadData();
  }, [loadData]);

  const bulkDelete = useCallback(async (ids) => {
    if (!window.confirm(`Supprimer ${ids.length} visiteur(s) ? Cette action vide "visiteur_amo" sur les dossiers concernés.`)) return;
    let ok = 0, errs = 0;
    for (const name of ids) {
      try {
        const res = await fetch(`${API}/api/metier/hub-grids/visiteurs-stats/${encodeURIComponent(name)}`, {
          method: "DELETE", headers: getAuthHeaders(), credentials: "include",
        });
        if (res.ok) ok++; else errs++;
      } catch { errs++; }
    }
    if (ok) toast.success(`${ok} visiteur(s) supprimé(s)`);
    if (errs) toast.error(`${errs} erreur(s)`);
    loadData();
  }, [loadData]);

  // ─── Création ───
  const handleCreate = async () => {
    if (!createForm.prenom || !createForm.nom) { toast.error("Prénom et nom requis"); return; }
    setCreating(true);
    try {
      const res = await fetch(`${API}/api/metier/op-visiteurs`, {
        method: "POST", headers: getAuthHeaders(), credentials: "include",
        body: JSON.stringify(createForm),
      });
      if (res.ok) {
        toast.success(`Visiteur ${createForm.prenom} ${createForm.nom} créé`);
        setShowCreate(false);
        setCreateForm({ prenom: "", nom: "", email: "", telephone: "" });
        loadData();
      } else toast.error("Erreur création");
    } catch { toast.error("Erreur réseau"); }
    finally { setCreating(false); }
  };

  // ─── Config OHGrid ───
  const stats = useMemo(() => {
    const tot = visibleRows.length;
    const actifs = visibleRows.filter(v => !v.archived).length;
    const archives = enriched.filter(v => v.archived).length;
    const avecDossiers = visibleRows.filter(v => v.nb_dossiers > 0).length;
    const totalAMO = visibleRows.reduce((a, v) => a + (v.montant_total_amo || 0), 0);
    return [
      { key: "total", label: "Total", value: tot, icon: Users, color: "purple", filter: null },
      { key: "actifs", label: "Actifs", value: actifs, icon: CheckCircle2, color: "green",
        filter: (row) => !row.archived },
      { key: "archived", label: "Archivés", value: archives, icon: Archive, color: "orange",
        filter: (row) => row.archived },
      { key: "avec_dossiers", label: "Avec dossiers", value: avecDossiers, icon: FileText, color: "blue",
        filter: (row) => row.nb_dossiers > 0 },
      // ⛔ La tuile ⛔ n'apparaît QUE pour un tenant qui déclare l'AMO.
      ...(hasDossierAmo()
        ? [{ key: "amo_total", label: "AMO total", value: `${totalAMO.toLocaleString("fr-FR")} €`, icon: Euro, color: "cyan", filter: null }]
        : []),
    ];
  }, [visibleRows, enriched]);

  const advancedFilters = useMemo(() => [
    { key: "archived", label: "Inclure archivés", type: "checkbox", defaultValue: false },
  ], []);

  const handleAdvFilter = useCallback(({ key, value }) => {
    if (key === "archived") setShowArchived(value);
  }, []);

  const searchFilter = useCallback((row, q) =>
    (row.visiteur || "").toLowerCase().includes(q), []);

  const columns = useMemo(() => [
    {
      key: "visiteur", label: "Visiteur",
      render: (v, row) => (
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-full bg-emerald-500/20 flex items-center justify-center shrink-0">
            <User className="w-3.5 h-3.5 text-emerald-400" />
          </div>
          <span className="font-medium" style={{ color: row.archived ? "var(--oh-text-muted)" : "var(--oh-text)" }}>{v}</span>
          {row.archived && <Badge className="text-[9px] bg-amber-500/15 text-amber-400 border-amber-500/30">Archivé</Badge>}
        </div>
      ),
    },
    { key: "nb_dossiers", label: "Dossiers",
      render: (v) => <span className="font-semibold">{v}</span> },
    { key: "nb_visites", label: "Visites",
      render: (v) => <span className="text-xs" style={{ color: "var(--oh-text-muted)" }}>{v}</span> },
    {
      key: "_pct_payee_client", label: "Visite payée", sortable: false,
      render: (_, row) => (
        <div className="flex items-center gap-2">
          <ProgressMini pct={row._pct_payee_client} />
          <span className="text-xs w-14 text-right font-medium"
            style={{ color: row._pct_payee_client >= 80 ? "#10b981" : row._pct_payee_client >= 50 ? "var(--oh-stat-amber-text)" : "var(--oh-stat-red-text)" }}>
            {row.visites_payees_client}/{row.nb_visites}
          </span>
        </div>
      ),
    },
    // 🔴🔴 N°728-1 — LES DEUX COLONNES D'AMO, gatées par la CAPACITÉ DÉCLARÉE.
    // ⚠️ « TEF payé » nomme un AUTRE TENANT et décrit un geste d'AMO (le donneur
    // d'ordre verse au visiteur) ; « Montant AMO » est l'honoraire d'AMO. Les
    // deux étaient servis à tout le monde, ⛔ sans condition.
    // 🔑 ⛔ On ⛔ ne CHANGE PAS le libellé de TEF (non-régression stricte : il
    // déclare la capacité, il voit EXACTEMENT ce qu'il voyait). Les autres
    // tenants ⛔ ne le voient plus : c'est la dépollution demandée.
    ...(hasDossierAmo() ? [
    {
      key: "_pct_tef_paye", label: "TEF payé", sortable: false,
      render: (_, row) => (
        <div className="flex items-center gap-2">
          <ProgressMini pct={row._pct_tef_paye} />
          <span className="text-xs w-14 text-right font-medium"
            style={{ color: row._pct_tef_paye >= 80 ? "#10b981" : row._pct_tef_paye >= 50 ? "var(--oh-stat-amber-text)" : "var(--oh-stat-red-text)" }}>
            {row.tef_paye_visiteur}/{row.nb_visites}
          </span>
        </div>
      ),
    },
    {
      key: "montant_total_amo", label: "Montant AMO",
      render: (v) => <span className="font-medium" style={{ color: "var(--oh-text)" }}>{v ? `${v.toLocaleString("fr-FR")} €` : "—"}</span>,
    },
    ] : []),
    { key: "avec_facture", label: "Avec facture",
      render: (v) => <span className="text-xs" style={{ color: "var(--oh-text-muted)" }}>{v}</span> },
  ], []);

  const rowActions = useMemo(() => [
    { label: "Voir la fiche", icon: Eye, onClick: (row) => setSelectedVisiteur(row.visiteur) },
    { label: "Archiver", icon: Archive, onClick: (row) => handleArchive(row, true),
      show: (row) => !row.archived, className: "text-amber-400" },
    { label: "Réactiver", icon: ArchiveRestore, onClick: (row) => handleArchive(row, false),
      show: (row) => row.archived, className: "text-blue-400" },
    { label: "Supprimer", icon: Trash2, onClick: handleDelete, className: "text-red-400" },
  ], [handleArchive, handleDelete]);

  const bulkActions = useMemo(() => [
    { label: "Archiver", icon: Archive, onClick: (ids) => bulkArchive(ids, true),
      className: "border-amber-600 text-amber-400 hover:bg-amber-600/20" },
    { label: "Réactiver", icon: ArchiveRestore, onClick: (ids) => bulkArchive(ids, false),
      className: "border-blue-600 text-blue-400 hover:bg-blue-600/20" },
    { label: "Supprimer", icon: Trash2, onClick: bulkDelete,
      className: "border-red-600 text-red-400 hover:bg-red-600/20" },
  ], [bulkArchive, bulkDelete]);

  if (selectedVisiteur) {
    return <VisiteurFiche visiteurName={selectedVisiteur} onBack={() => setSelectedVisiteur(null)} onRefreshList={loadData} />;
  }

  return (
    <div className="space-y-4" data-testid="visiteurs-manager">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-emerald-500/10">
            <Users className="w-5 h-5 text-emerald-400" />
          </div>
          <div>
            <h2 className="text-lg font-semibold" style={{ color: "var(--oh-text)" }}>{hasDossierAmo() ? "Visiteurs AMO" : "Visiteurs"}</h2>
            <p className="text-xs" style={{ color: "var(--oh-text-muted)" }}>
              {data.total_visiteurs} visiteur{data.total_visiteurs > 1 ? "s" : ""} · {data.total_dossiers} dossier{data.total_dossiers > 1 ? "s" : ""}
            </p>
          </div>
        </div>
        <Button onClick={() => setShowCreate(true)} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="add-visiteur-btn">
          <UserPlus className="w-4 h-4 mr-2" /> Ajouter un visiteur
        </Button>
      </div>

      <OHGrid
        data={visibleRows}
        columns={columns}
        stats={stats}
        advancedFilters={advancedFilters}
        onAdvancedFilter={handleAdvFilter}
        rowActions={rowActions}
        bulkActions={bulkActions}
        selectable={true}
        pagination={true}
        pageSize={25}
        loading={loading}
        onRefresh={loadData}
        onRowClick={(row) => setSelectedVisiteur(row.visiteur)}
        searchPlaceholder="Rechercher un visiteur..."
        searchFilter={searchFilter}
        defaultSortKey="nb_dossiers"
        defaultSortDir="desc"
        emptyMessage="Aucun visiteur trouvé"
        testIdPrefix="visiteurs"
      />

      {/* Modale création */}
      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={() => setShowCreate(false)}>
          <div className="rounded-xl p-6 w-full max-w-md space-y-4"
            style={{ background: "var(--oh-bg-elevated)", border: "1px solid var(--oh-border)" }}
            onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-bold flex items-center gap-2" style={{ color: "var(--oh-text)" }}>
              <UserPlus className="w-5 h-5 text-emerald-400" /> Ajouter un visiteur
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs mb-1 block" style={{ color: "var(--oh-text-muted)" }}>Prénom *</label>
                <Input value={createForm.prenom}
                  onChange={e => setCreateForm(f => ({ ...f, prenom: e.target.value }))}
                  onBlur={e => setCreateForm(f => ({ ...f, prenom: normalizeName(e.target.value, "prenom") }))}
                  placeholder="Jordan"
                  style={{ background: "var(--oh-bg-surface)", borderColor: "var(--oh-border)", color: "var(--oh-text)" }}
                  data-testid="create-visiteur-prenom" />
              </div>
              <div>
                <label className="text-xs mb-1 block" style={{ color: "var(--oh-text-muted)" }}>Nom *</label>
                <Input value={createForm.nom}
                  onChange={e => setCreateForm(f => ({ ...f, nom: e.target.value }))}
                  onBlur={e => setCreateForm(f => ({ ...f, nom: normalizeName(e.target.value, "nom") }))}
                  placeholder="NOM EXEMPLE"
                  style={{ background: "var(--oh-bg-surface)", borderColor: "var(--oh-border)", color: "var(--oh-text)" }}
                  data-testid="create-visiteur-nom" />
              </div>
              <div>
                <label className="text-xs mb-1 block" style={{ color: "var(--oh-text-muted)" }}>Email</label>
                <Input type="email" value={createForm.email} onChange={e => setCreateForm(f => ({ ...f, email: e.target.value }))} placeholder="email@..."
                  style={{ background: "var(--oh-bg-surface)", borderColor: "var(--oh-border)", color: "var(--oh-text)" }} />
              </div>
              <div>
                <label className="text-xs mb-1 block" style={{ color: "var(--oh-text-muted)" }}>Téléphone</label>
                <Input type="tel" value={createForm.telephone} onChange={e => setCreateForm(f => ({ ...f, telephone: e.target.value }))} placeholder="06..."
                  style={{ background: "var(--oh-bg-surface)", borderColor: "var(--oh-border)", color: "var(--oh-text)" }} />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" onClick={() => setShowCreate(false)}>Annuler</Button>
              <Button onClick={handleCreate} disabled={creating} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="create-visiteur-submit">
                {creating ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <UserPlus className="w-4 h-4 mr-1" />} Créer
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
